import React, { useState, useEffect, useContext, createContext, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet, apiPost, apiPut } from "../utils/apiAuth";
import config from '../config';
import { ContentTypeConstants } from '../constants/contentCreationConstants';
import { parseQuizQuestionData } from '../utils/quizForm';
import {
  makeContentBankCacheKey,
  getContentBankCache,
  setContentBankCache,
  invalidateContentBankCache,
} from '../utils/contentBankTableCache';
import JoditProEditor from 'jodit-pro-react';
import CommentPopup from './CommentPopup';
import ClonePopup from './ClonePopup';
import { toast } from './Toast';
import { ConfirmDialog, Select, EmptyState, StatusBadge } from './ui';
import { 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  Paper, Box, Typography, IconButton, Tooltip, TablePagination, 
  Checkbox, Button, Chip, CircularProgress, Alert, Collapse,
  Tabs, Tab,
} from '@mui/material';
import { 
  Add as AddIcon,
  Comment as CommentIcon,
  FileCopy as FileCopyIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  FilterListOutlined as FilterListOutlinedIcon,
} from '@mui/icons-material';

const layoutStyles = {
  container: { height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  tabContent: { flex: 1, minHeight: 0, overflow: 'auto', p: 2 },
};

const TABLE_STYLES = {
  size: 'medium',
  tableSx: {
    '& .MuiTableCell-head': {
      fontWeight: 600,
      fontSize: '0.8125rem',
      py: 1.25,
      bgcolor: 'var(--color-bg-subtle)',
      color: 'var(--color-text-secondary)',
      borderBottom: '1px solid var(--color-border-default)',
    },
    '& .MuiTableCell-body': {
      fontSize: '0.8125rem',
      py: 1.25,
      borderBottom: '1px solid var(--color-border-default)',
    },
  },
};

const tableStyles = {
  card: { backgroundColor: "#fff", borderRadius: "8px", boxShadow: "0 2px 4px rgba(0,0,0,0.08)" },
  headerCell: { backgroundColor: "#f8f9fa", fontWeight: 600, color: "#333", borderBottom: "2px solid #dee2e6" },
  accordionToggle: { border: "none", background: "transparent", cursor: "pointer", fontSize: "13px", color: "#666", padding: "4px 6px", borderRadius: "4px" },
  actionButton: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: "30px", height: "30px", border: "1px solid #d0d7de", borderRadius: "50%", backgroundColor: "#fff", cursor: "pointer", color: "#495057", position: "relative" },
  addButton: { color: "#28a745", borderColor: "#28a745" },
  cloneIcon: { color: "#6c757d" },
  expandedCell: { backgroundColor: "#f8f9fa", padding: 0 },
  videoContainer: { padding: "20px", backgroundColor: "#fff", borderRadius: "8px", margin: "10px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" },
  noVideo: { textAlign: "center", color: "#666", padding: "40px", backgroundColor: "#f8f9fa", borderRadius: "4px" },
  sectionCard: { backgroundColor: "#fff", borderRadius: "8px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)", padding: "16px" },
  testsBadge: { backgroundColor: "#e3f2fd", color: "#1976d2", borderRadius: "999px", padding: "2px 10px", fontWeight: 600, fontSize: "12px" },
  checkButton: { borderRadius: "20px" },
};

const iconStyles = {
  messageCount: { position: "absolute", top: "-6px", right: "-6px", backgroundColor: "#dc3545", color: "#fff", borderRadius: "999px", padding: "0 5px", fontSize: "10px", fontWeight: 600 },
};

const AddNewContentButton = ({ onClick, label = 'Add content' }) => (
  <Tooltip title={label}>
    <IconButton
      onClick={onClick}
      aria-label={label}
      sx={{
        color: '#28a745',
        border: '1px solid #28a745',
        width: 40,
        height: 40,
      }}
    >
      <AddIcon />
    </IconButton>
  </Tooltip>
);

function navigateToNewContent({ navigate, subjects, filters, contentType }) {
  if (!filters.subject_id) {
    toast.warning('Please select a subject first');
    return;
  }

  if (!filters.topic_id) {
    toast.warning('Please select a topic first');
    return;
  }

  const selectedSubject = subjects.find((subject) => subject.subject_id === filters.subject_id);
  const subjectName = selectedSubject ? selectedSubject.subject_name : '';

  sessionStorage.setItem('contentCreation_contentType', JSON.stringify(contentType));
  sessionStorage.setItem('contentCreation_selectedDifficulty', JSON.stringify(filters.level || 'level1'));
  sessionStorage.setItem('contentCreation_selectedSubTopic', JSON.stringify(filters.sub_topic_id || ''));
  sessionStorage.setItem('contentCreation_selectedTopic', JSON.stringify(filters.topic_id));
  sessionStorage.setItem('contentCreation_subjectId', JSON.stringify(filters.subject_id));
  sessionStorage.setItem('contentCreation_subjectName', JSON.stringify(subjectName));
  sessionStorage.setItem('contentCreation_navSource', 'content-bank');
  sessionStorage.removeItem('contentCreation_selectedVideo');
  sessionStorage.removeItem('contentCreation_selectedNote');
  sessionStorage.removeItem('contentCreation_selectedQuestion');
  sessionStorage.removeItem('currentQuestionId');
  sessionStorage.removeItem('contentCreation_selectedEditor');
  sessionStorage.removeItem('contentCreation_projectCodingDescription');
  sessionStorage.removeItem('contentCreation_freezeFilters');
  sessionStorage.removeItem('contentCreation_projectId');
  sessionStorage.removeItem('contentCreation_projectName');

  navigate('/content-creation', {
    state: {
      data: {
        subject_id: filters.subject_id,
        subject_name: subjectName,
        freezeFilters: false,
      },
    },
  });
}

// Check if user has AD role
const isAdmin = () => {
  const userType = sessionStorage.getItem('Type') || '';
  return userType.includes('AD');
};

// Create Context for filter state management
const FilterContext = createContext();

// Create Context for refresh management
const RefreshContext = createContext();

// Refresh Context Provider
export const RefreshProvider = ({ children }) => {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  const triggerRefresh = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  return (
    <RefreshContext.Provider value={{ refreshTrigger, triggerRefresh }}>
      {children}
    </RefreshContext.Provider>
  );
};

// Filter Context Provider
export const FilterProvider = ({ children }) => {
  const [filters, setFilters] = useState({
    subject_id: null,
    topic_id: null,
    sub_topic_id: null,
    level: null
  });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [subjects, setSubjects] = useState([]);

  // Fetch subjects on mount
  useEffect(() => {
    fetchSubjects();
  }, []);

  const fetchSubjects = async () => {
    try {
      const response = await apiGet(`${config.API_BASE_URL}/api/get-subjects/`);
      setSubjects(response.data.subjects || []);
    } catch (error) {
    }
  };

  // Initialize filters from sessionStorage on mount
  useEffect(() => {
    try {
      const subjectId = sessionStorage.getItem('contentBank_subjectId')
        ? JSON.parse(sessionStorage.getItem('contentBank_subjectId'))
        : null;
      const topicId = sessionStorage.getItem('contentBank_topicId')
        ? JSON.parse(sessionStorage.getItem('contentBank_topicId'))
        : null;
      const subTopicId = sessionStorage.getItem('contentBank_subTopicId')
        ? JSON.parse(sessionStorage.getItem('contentBank_subTopicId'))
        : null;
      const level = sessionStorage.getItem('contentBank_level')
        ? JSON.parse(sessionStorage.getItem('contentBank_level'))
        : null;

      setFilters({
        subject_id: subjectId,
        topic_id: topicId,
        sub_topic_id: subTopicId,
        level: level
      });
    } catch (e) { /* ignore */ }
    
    // Mark that initial load is complete
    setTimeout(() => {
      setIsInitialLoad(false);
    }, 100);
  }, []);

  const updateFilter = (key, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      
      // Reset dependent filters when parent changes
      if (key === 'subject_id') {
        newFilters.topic_id = null;
        newFilters.sub_topic_id = null;
      } else if (key === 'topic_id') {
        newFilters.sub_topic_id = null;
      }
      
      // Persist filters in sessionStorage (remove keys when cleared)
      try {
        const persist = (storageKey, val) => {
          if (val) sessionStorage.setItem(storageKey, JSON.stringify(val));
          else sessionStorage.removeItem(storageKey);
        };
        persist('contentBank_subjectId', newFilters.subject_id);
        persist('contentBank_topicId', newFilters.topic_id);
        persist('contentBank_subTopicId', newFilters.sub_topic_id);
        persist('contentBank_level', newFilters.level);
      } catch (e) { /* ignore */ }
      
      return newFilters;
    });
  };

  const resetFilters = () => {
    setFilters({
      subject_id: null,
      topic_id: null,
      sub_topic_id: null,
      level: null
    });
    try {
      sessionStorage.removeItem('contentBank_subjectId');
      sessionStorage.removeItem('contentBank_topicId');
      sessionStorage.removeItem('contentBank_subTopicId');
      sessionStorage.removeItem('contentBank_level');
    } catch (e) { /* ignore */ }
  };

  return (
    <FilterContext.Provider value={{ filters, updateFilter, resetFilters, isInitialLoad, subjects }}>
      {children}
    </FilterContext.Provider>
  );
};

// Custom hook to use filter context
export const useFilters = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error('useFilters must be used within a FilterProvider');
  }
  return context;
};

// Hierarchical Filter Component
const HierarchicalFilters = () => {
  const { filters, updateFilter, isInitialLoad, subjects } = useFilters();
  const [topics, setTopics] = useState([]);
  const [subTopics, setSubTopics] = useState([]);
  const [loading, setLoading] = useState({
    topics: false,
    subTopics: false
  });

  const levels = [
    { value: 'level1', label: 'Level 1' },
    { value: 'level2', label: 'Level 2' },
    { value: 'level3', label: 'Level 3' }
  ];

  // Handle filter changes and pre-populated data (only after initial load)
  useEffect(() => {
    if (!isInitialLoad) {
      if (filters.subject_id && filters.topic_id) {
        // If both subject and topic are available, fetch topics first, then sub-topics
        fetchTopics(filters.subject_id).then(() => {
          if (filters.sub_topic_id) {
            fetchSubTopics(filters.topic_id);
          }
        });
      } else if (filters.subject_id) {
        // If only subject is available, fetch topics
        fetchTopics(filters.subject_id);
        setSubTopics([]);
      } else {
        // No subject selected, clear everything
        setTopics([]);
        setSubTopics([]);
      }
    }
  }, [filters.subject_id, filters.topic_id, filters.sub_topic_id, isInitialLoad]);

  // Handle topic changes separately to avoid conflicts
  useEffect(() => {
    if (!isInitialLoad && filters.topic_id && !filters.sub_topic_id) {
      // Only fetch sub-topics if topic changed and no sub-topic is pre-selected
      fetchSubTopics(filters.topic_id);
    } else if (!isInitialLoad && !filters.topic_id) {
      // Clear sub-topics if no topic selected
      setSubTopics([]);
    }
  }, [filters.topic_id, isInitialLoad]);

  const fetchTopics = async (subjectId) => {
    try {
      setLoading(prev => ({ ...prev, topics: true }));
      const response = await apiPost(`${config.API_BASE_URL}/api/get-subject-topics/`, {
        subject_id: subjectId
      });
      setTopics(response.data.topics || []);
      return response.data.topics || [];
    } catch (error) {
      return [];
    } finally {
      setLoading(prev => ({ ...prev, topics: false }));
    }
  };

  const fetchSubTopics = async (topicId) => {
    try {
      setLoading(prev => ({ ...prev, subTopics: true }));
      const response = await apiPost(`${config.API_BASE_URL}/api/get-topic-subtopics/`, {
        topic_id: topicId
      });
      setSubTopics(response.data.sub_topics || []);
      return response.data.sub_topics || [];
    } catch (error) {
      return [];
    } finally {
      setLoading(prev => ({ ...prev, subTopics: false }));
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: { xs: 'wrap', lg: 'nowrap' },
          gap: 2,
          alignItems: 'flex-start',
          width: '100%',
          minWidth: 0,
        }}
      >
        <Select
          id="content-bank-filter-subject"
          label="Subject"
          value={filters.subject_id || ''}
          onChange={(e) => updateFilter('subject_id', e.target.value || null)}
          disabled={loading.subjects}
          placeholder="Select Subject"
          displayEmpty
          fullWidth={false}
          options={subjects.map((subject) => ({
            value: subject.subject_id,
            label: subject.subject_name,
          }))}
          sx={{ flex: '1 1 0', minWidth: 0 }}
        />

        <Select
          id="content-bank-filter-topic"
          label="Topic"
          value={filters.topic_id || ''}
          onChange={(e) => updateFilter('topic_id', e.target.value || null)}
          disabled={!filters.subject_id || loading.topics}
          placeholder="Select Topic"
          displayEmpty
          fullWidth={false}
          options={topics.map((topic) => ({
            value: topic.topic_id,
            label: topic.topic_name,
          }))}
          sx={{ flex: '1 1 0', minWidth: 0 }}
        />

        <Select
          id="content-bank-filter-subtopic"
          label="Sub Topic"
          value={filters.sub_topic_id || ''}
          onChange={(e) => updateFilter('sub_topic_id', e.target.value || null)}
          disabled={!filters.topic_id || loading.subTopics}
          placeholder="Select Sub Topic"
          displayEmpty
          fullWidth={false}
          options={subTopics.map((subTopic) => ({
            value: subTopic.sub_topic_id,
            label: subTopic.sub_topic_name,
          }))}
          sx={{ flex: '1 1 0', minWidth: 0 }}
        />

        <Select
          id="content-bank-filter-level"
          label="Level"
          value={filters.level || ''}
          onChange={(e) => updateFilter('level', e.target.value || null)}
          placeholder="Select Level"
          displayEmpty
          fullWidth={false}
          options={levels}
          sx={{ flex: '1 1 0', minWidth: 0 }}
        />
      </Box>

      {(filters.subject_id || filters.topic_id || filters.sub_topic_id || filters.level) && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2, alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
            Active:
          </Typography>
          {filters.subject_id && (
            <Chip
              size="small"
              label={`Subject: ${subjects.find((s) => s.subject_id === filters.subject_id)?.subject_name || 'Selected'}`}
              onDelete={() => updateFilter('subject_id', null)}
            />
          )}
          {filters.topic_id && (
            <Chip
              size="small"
              label={`Topic: ${topics.find((t) => t.topic_id === filters.topic_id)?.topic_name || 'Selected'}`}
              onDelete={() => updateFilter('topic_id', null)}
            />
          )}
          {filters.sub_topic_id && (
            <Chip
              size="small"
              label={`Sub-topic: ${subTopics.find((st) => st.sub_topic_id === filters.sub_topic_id)?.sub_topic_name || 'Selected'}`}
              onDelete={() => updateFilter('sub_topic_id', null)}
            />
          )}
          {filters.level && (
            <Chip
              size="small"
              label={`Level: ${levels.find((l) => l.value === filters.level)?.label || filters.level}`}
              onDelete={() => updateFilter('level', null)}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

// Videos Table Component
const VideosTable = ({ isAdmin }) => {
  const navigate = useNavigate();
  const { filters, isInitialLoad, subjects } = useFilters();
  const { refreshTrigger } = useContext(RefreshContext);
  const { size, tableSx } = TABLE_STYLES;
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [allExpanded, setAllExpanded] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    videoId: null,
    currentVerified: null
  });
  const [errorModal, setErrorModal] = useState({
    isOpen: false,
    message: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const fetchVideos = useCallback(async ({ force = false } = {}) => {
    if (!filters.subject_id) return;

    const cacheKey = makeContentBankCacheKey('video', filters, pagination);
    // Stale-while-revalidate: paint cache immediately, then always refetch quietly
    let hadCache = false;
    if (!force) {
      const cached = getContentBankCache(cacheKey);
      if (cached) {
        hadCache = true;
        setVideos(cached.rows || []);
        setPagination((prev) => ({
          ...prev,
          total: cached.total || 0,
          totalPages: cached.totalPages || 0,
        }));
        setLoading(false);
        setError(null);
      }
    }

    try {
      if (!hadCache) setLoading(true);
      setError(null);
      
      const payload = {
        subject_id: filters.subject_id,
        page: pagination.page,
        limit: pagination.limit
      };

      // Add optional filters
      if (filters.topic_id) payload.topic_id = filters.topic_id;
      if (filters.sub_topic_id) payload.sub_topic_id = filters.sub_topic_id;
      if (filters.level) payload.level = filters.level;

      const response = await apiPost(`${config.API_BASE_URL}/api/get-videos-hierarchical/`, payload);
      const rows = response.data.videos || [];
      const total = response.data.total_videos || 0;
      const totalPages = response.data.total_pages || 0;

      setVideos(rows);
      setPagination(prev => ({
        ...prev,
        total,
        totalPages,
      }));
      setContentBankCache(cacheKey, { rows, total, totalPages });
    } catch (error) {
      if (!hadCache) setError("Failed to fetch videos");
    } finally {
      setLoading(false);
    }
  }, [filters.subject_id, filters.topic_id, filters.sub_topic_id, filters.level, pagination.page, pagination.limit]);

  // Handle + icon click
  const handleAddContent = (video) => {
    try {
      // Validate required filters
      if (!filters.subject_id) {
        toast.warning('Please select a subject first');
        return;
      }
      
      if (!filters.topic_id) {
        toast.warning('Please select a topic first');
        return;
      }
      
      // Find the subject name from the subjects array
      const selectedSubject = subjects.find(subject => subject.subject_id === filters.subject_id);
      const subjectName = selectedSubject ? selectedSubject.subject_name : '';
      
      // Set session storage with content creation parameters
      sessionStorage.setItem('contentCreation_contentType', JSON.stringify(ContentTypeConstants.VIDEO.key));
      sessionStorage.setItem('contentCreation_selectedDifficulty', JSON.stringify(video?.level || filters.level || 'level1'));
      sessionStorage.setItem('contentCreation_selectedSubTopic', JSON.stringify(filters.sub_topic_id || ''));
      sessionStorage.setItem('contentCreation_selectedTopic', JSON.stringify(filters.topic_id));
      if (video?.id) {
        sessionStorage.setItem('contentCreation_selectedVideo', JSON.stringify(video.id.toString()));
      } else {
        sessionStorage.removeItem('contentCreation_selectedVideo');
      }
      sessionStorage.setItem('contentCreation_subjectId', JSON.stringify(filters.subject_id));
      sessionStorage.setItem('contentCreation_subjectName', JSON.stringify(subjectName));
      
      // Mark subject flow so sidebar/back stay on Content Bank
      sessionStorage.setItem('contentCreation_navSource', 'content-bank');
      sessionStorage.removeItem('contentCreation_freezeFilters');
      sessionStorage.removeItem('contentCreation_projectId');
      sessionStorage.removeItem('contentCreation_projectName');
      
      // Navigate to content creation page with subject data
      navigate('/content-creation', {
        state: {
          data: {
            subject_id: filters.subject_id,
            subject_name: subjectName,
            freezeFilters: false // Explicitly set to false to ensure filters are not frozen
          }
        }
      });
    } catch (error) {
      toast.error('Error occurred while navigating to content creation');
    }
  };

  // Fetch videos when filters change or on initial load with pre-populated filters
  useEffect(() => {
    if (filters.subject_id) {
      fetchVideos();
    } else if (!isInitialLoad) {
      setVideos([]);
      setPagination({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
      });
    }
  }, [fetchVideos, filters.subject_id, filters.topic_id, filters.sub_topic_id, filters.level, pagination.page, pagination.limit]);

  // Refresh videos when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger > 0) {
      invalidateContentBankCache('video');
      fetchVideos({ force: true });
    }
  }, [refreshTrigger, fetchVideos]);

  const toggleRow = (videoId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(videoId)) {
      newExpandedRows.delete(videoId);
    } else {
      newExpandedRows.add(videoId);
    }
    setExpandedRows(newExpandedRows);
  };

  const toggleAllRows = () => {
    if (allExpanded) {
      setExpandedRows(new Set());
    } else {
      setExpandedRows(new Set(videos.map(video => video.id)));
    }
    setAllExpanded(!allExpanded);
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleVerifiedChange = (videoId, currentVerified) => {
    if (!isAdmin) return;
    setConfirmationModal({
      isOpen: true,
      videoId,
      currentVerified
    });
  };

  const handleConfirmVerifiedChange = async () => {
    const { videoId, currentVerified } = confirmationModal;
    const newVerified = !currentVerified;
    
    try {
      await apiPut(`${config.API_BASE_URL}/api/update-verified/`, {
        content_type: 'video',
        content_id: String(videoId),
        verified: newVerified
      });
      
      // Update local state + cache so Back does not show stale verified flags
      setVideos((prev) => {
        const rows = prev.map((v) =>
          v.id === videoId ? { ...v, verified: newVerified } : v
        );
        setContentBankCache(makeContentBankCacheKey('video', filters, pagination), {
          rows,
          total: pagination.total,
          totalPages: pagination.totalPages,
        });
        return rows;
      });
      
      // Close modal
      setConfirmationModal({ isOpen: false, videoId: null, currentVerified: null });
    } catch (error) {
      setConfirmationModal({ isOpen: false, videoId: null, currentVerified: null });
      setErrorModal({ isOpen: true, message: 'Failed to update verified status. Please try again.' });
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0 mins';
    const minutes = Math.floor(seconds / 60);
    return `${minutes} mins`;
  };

  if (!filters.subject_id) {
    return (
      <EmptyState
        compact
        icon={FilterListOutlinedIcon}
        title="Select a subject"
        description="Choose a subject from the filters above to browse videos."
      />
    );
  }

  if (!loading && videos.length === 0) {
    return (
      <EmptyState
        compact
        title="No videos found"
        description="Try adjusting topic, sub-topic, or level filters, or add new content."
        action={<AddNewContentButton onClick={() => navigateToNewContent({ navigate, subjects, filters, contentType: ContentTypeConstants.VIDEO.key })} label="Add video" />}
      />
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 'var(--radius-md)' }}>
        <Table size={size} sx={{ minWidth: 650, ...tableSx }} aria-label="videos table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "50px" }}>
                <IconButton
                  size="small"
                  onClick={toggleAllRows}
                  title={allExpanded ? "Collapse All" : "Expand All"}
                >
                  {allExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
                </IconButton>
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>S.no</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>Title</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>Sequence</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>Level</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>Duration</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>Verified</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '13px' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {videos.map((video, index) => (
              <React.Fragment key={video.id}>
                <TableRow sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => toggleRow(video.id)}
                      title={expandedRows.has(video.id) ? "Collapse" : "Expand"}
                    >
                      {expandedRows.has(video.id) ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>{(pagination.page - 1) * pagination.limit + index + 1}</TableCell>
                  <TableCell>{video.title || 'Untitled'}</TableCell>
                  <TableCell>{video.sequence || '-'}</TableCell>
                  <TableCell>{video.level || '-'}</TableCell>
                  <TableCell>{video.duration ? `${video.duration} min` : '-'}</TableCell>
                  <TableCell><StatusBadge status={video.status} /></TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={video.verified || false}
                      disabled={!isAdmin}
                      onChange={() => handleVerifiedChange(video.id, video.verified)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <Tooltip title="Add Content">
                        <IconButton
                          size="small"
                        onClick={() => handleAddContent(video)}
                          sx={{ color: '#28a745' }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Comments">
                        <IconButton
                          size="small"
                        onClick={() => handleOpenCommentPopup('video', video.id)}
                          sx={{ position: 'relative' }}
                      >
                          <CommentIcon fontSize="small" />
                        {video.resolved !== null && video.resolved > 0 && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: '-6px',
                                right: '-6px',
                                backgroundColor: '#dc3545',
                                color: '#fff',
                                borderRadius: '999px',
                                padding: '0 5px',
                                fontSize: '10px',
                                fontWeight: 600,
                                minWidth: '18px',
                                height: '18px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              {video.resolved}
                            </Box>
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Clone Content">
                        <IconButton
                          size="small"
                        onClick={() => handleOpenClonePopup('video', video.id)}
                          sx={{ color: '#6c757d' }}
                        >
                          <FileCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={9} sx={{ py: 0, borderBottom: 'none' }}>
                    <Collapse in={expandedRows.has(video.id)} timeout="auto" unmountOnExit>
                      <Box sx={{ padding: "20px", backgroundColor: "#fff", borderRadius: "8px", margin: "10px" }}>
                        {video.vdo_video_id && video.vdo_otp && video.vdo_playback_info ? (
                          <iframe
                            src={`https://player.vdocipher.com/v2/?otp=${video.vdo_otp}&playbackInfo=${video.vdo_playback_info}`}
                            width="100%"
                            height="400"
                            frameBorder="0"
                            allowFullScreen
                            title="Video Player"
                            allow="encrypted-media"
                            style={{ border: '1px solid #ddd', borderRadius: '4px' }}
                          />
                        ) : (
                          <Box sx={{ textAlign: "center", color: "#666", padding: "40px", backgroundColor: "#f8f9fa", borderRadius: "4px" }}>
                            <Typography>Video not available</Typography>
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                          <Button variant="contained" color="success" size="small" title="Clone Video">
                            📋 Clone
                          </Button>
                        </Box>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination.totalPages > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 3, mt: 3 }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
          >
            Previous
          </Button>
          <Typography variant="body2" color="text.secondary">
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} total videos)
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page === pagination.totalPages}
          >
            Next
          </Button>
        </Box>
      )}

      <ConfirmDialog
        open={confirmationModal.isOpen}
        onClose={() => setConfirmationModal({ isOpen: false, videoId: null, currentVerified: null })}
        onConfirm={handleConfirmVerifiedChange}
        title="Confirm Verification Status Change"
        message={`Are you sure you want to ${confirmationModal.currentVerified ? 'unverify' : 'verify'} this video?\n\n`}
        severity="warning"
      />

      <ConfirmDialog
        open={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: '' })}
        title="Error"
        message={errorModal.message}
        variant="alert"
        severity="error"
      />
    </Box>
  );
};

// Notes Table Component
const NotesTable = ({ isAdmin }) => {
  const { filters, subjects } = useContext(FilterContext);
  const { refreshTrigger } = useContext(RefreshContext);
  const navigate = useNavigate();
  const { size, tableSx } = TABLE_STYLES;
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [allExpanded, setAllExpanded] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    noteId: null,
    currentVerified: null
  });
  const [errorModal, setErrorModal] = useState({
    isOpen: false,
    message: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const fetchNotes = useCallback(async ({ force = false } = {}) => {
    if (!filters.subject_id) return;

    const cacheKey = makeContentBankCacheKey('notes', filters, pagination);
    // Stale-while-revalidate: paint cache immediately, then always refetch quietly
    let hadCache = false;
    if (!force) {
      const cached = getContentBankCache(cacheKey);
      if (cached) {
        hadCache = true;
        setNotes(cached.rows || []);
        setPagination((prev) => ({
          ...prev,
          total: cached.total || 0,
          totalPages: cached.totalPages || 0,
        }));
        setLoading(false);
      }
    }
    
    if (!hadCache) setLoading(true);
    try {
      const payload = {
        subject_id: filters.subject_id,
        page: pagination.page,
        limit: pagination.limit
      };

      if (filters.topic_id) payload.topic_id = filters.topic_id;
      if (filters.sub_topic_id) payload.sub_topic_id = filters.sub_topic_id;
      if (filters.level) payload.level = filters.level;

      const response = await apiPost(`${config.API_BASE_URL}/api/get-contents-hierarchical/`, payload);
      
      if (response.status === 200) {
        const rows = response.data.contents || [];
        const total = response.data.total || 0;
        const totalPages = response.data.total_pages || 0;
        setNotes(rows);
        setPagination(prev => ({
          ...prev,
          total,
          totalPages,
        }));
        setContentBankCache(cacheKey, { rows, total, totalPages });
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [filters.subject_id, filters.topic_id, filters.sub_topic_id, filters.level, pagination.page, pagination.limit]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  // Refresh notes when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger > 0) {
      invalidateContentBankCache('notes');
      fetchNotes({ force: true });
    }
  }, [refreshTrigger, fetchNotes]);

  const toggleRow = (index) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  const toggleAllRows = () => {
    if (allExpanded) {
      setExpandedRows(new Set());
    } else {
      setExpandedRows(new Set(notes.map((_, index) => index)));
    }
    setAllExpanded(!allExpanded);
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleVerifiedChange = (noteId, currentVerified) => {
    if (!isAdmin) return;
    setConfirmationModal({
      isOpen: true,
      noteId,
      currentVerified
    });
  };

  const handleConfirmVerifiedChange = async () => {
    const { noteId, currentVerified } = confirmationModal;
    const newVerified = !currentVerified;
    
    try {
      await apiPut(`${config.API_BASE_URL}/api/update-verified/`, {
        content_type: 'notes',
        content_id: String(noteId),
        verified: newVerified
      });
      
      // Update local state + cache so Back does not show stale verified flags
      setNotes((prev) => {
        const rows = prev.map((n) =>
          n.id === noteId ? { ...n, verified: newVerified } : n
        );
        setContentBankCache(makeContentBankCacheKey('notes', filters, pagination), {
          rows,
          total: pagination.total,
          totalPages: pagination.totalPages,
        });
        return rows;
      });
      
      // Close modal
      setConfirmationModal({ isOpen: false, noteId: null, currentVerified: null });
    } catch (error) {
      setConfirmationModal({ isOpen: false, noteId: null, currentVerified: null });
      setErrorModal({ isOpen: true, message: 'Failed to update verified status. Please try again.' });
    }
  };

  const handleAddContent = (note) => {
    try {
      // Validate required filters
      if (!filters.subject_id) {
        toast.warning('Please select a subject first');
        return;
      }
      
      if (!filters.topic_id) {
        toast.warning('Please select a topic first');
        return;
      }
      
      // Find the subject name from the subjects array
      const selectedSubject = subjects.find(subject => subject.subject_id === filters.subject_id);
      const subjectName = selectedSubject ? selectedSubject.subject_name : '';
      
      // Set session storage with content creation parameters
      sessionStorage.setItem('contentCreation_contentType', JSON.stringify(ContentTypeConstants.NOTES.key));
      sessionStorage.setItem('contentCreation_selectedDifficulty', JSON.stringify(note?.level || filters.level || 'level1'));
      sessionStorage.setItem('contentCreation_selectedSubTopic', JSON.stringify(filters.sub_topic_id || ''));
      sessionStorage.setItem('contentCreation_selectedTopic', JSON.stringify(filters.topic_id));
      if (note?.id) {
        sessionStorage.setItem('contentCreation_selectedNote', JSON.stringify(note.id.toString()));
      } else {
        sessionStorage.removeItem('contentCreation_selectedNote');
      }
      sessionStorage.setItem('contentCreation_subjectId', JSON.stringify(filters.subject_id));
      sessionStorage.setItem('contentCreation_subjectName', JSON.stringify(subjectName));
      
      // Mark subject flow so sidebar/back stay on Content Bank
      sessionStorage.setItem('contentCreation_navSource', 'content-bank');
      sessionStorage.removeItem('contentCreation_freezeFilters');
      sessionStorage.removeItem('contentCreation_projectId');
      sessionStorage.removeItem('contentCreation_projectName');
      
      // Navigate to content creation page with subject data
      navigate('/content-creation', {
        state: {
          data: {
            subject_id: filters.subject_id,
            subject_name: subjectName,
            freezeFilters: false // Explicitly set to false to ensure filters are not frozen
          }
        }
      });
    } catch (error) {
      toast.error('Error occurred while navigating to content creation');
    }
  };

  // Jodit editor configuration for read-only mode
  const editorConfig = useMemo(() => ({
    license: '470CM-1R561-BSOP6-RRPCZ',
    readonly: true,
    height: 400,
    buttons: [],
    style: {
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      fontSize: "16px",
      lineHeight: "1.6",
      margin: "20px",
      color: "#333",
      height: "calc(100% - 10px)",
      overflow: "auto"
    },
    extraCSS: `
      .jodit-wysiwyg h1, .jodit-wysiwyg h2 {
        font-weight: 600;
        color: #2C3E50;
        margin-top: 30px;
      }
      .jodit-wysiwyg ul {
        margin: 10px 0 20px 20px;
      }
      .jodit-wysiwyg li {
        margin-bottom: 8px;
      }
      .jodit-wysiwyg code {
        background-color: #F4F4F4;
        padding: 4px 6px;
        font-family: Consolas, monospace;
        border-radius: 4px;
      }
      .jodit-wysiwyg table {
        border-collapse: collapse;
        width: 100%;
        margin: 20px 0;
      }
      .jodit-wysiwyg th, .jodit-wysiwyg td {
        border: 1px solid #ddd;
        padding: 12px;
        text-align: left;
      }
      .jodit-wysiwyg th {
        background-color: #F0F0F0;
      }
      .jodit-wysiwyg pre {
        background-color: #F8F8F8;
        padding: 12px;
        border-left: 3px solid #ccc;
        font-size: 15px;
        overflow-x: auto;
      }
    `,
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,
    disablePlugins: 'powered-by-jodit'
  }), []);

  if (!filters.subject_id) {
    return (
      <EmptyState
        compact
        icon={FilterListOutlinedIcon}
        title="Select a subject"
        description="Choose a subject from the filters above to browse notes."
      />
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 5 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading notes...
        </Typography>
      </Box>
    );
  }

  if (notes.length === 0) {
    return (
      <EmptyState
        compact
        title="No notes found"
        description="Try adjusting topic, sub-topic, or level filters, or add new content."
        action={<AddNewContentButton onClick={() => navigateToNewContent({ navigate, subjects, filters, contentType: ContentTypeConstants.NOTES.key })} label="Add notes" />}
      />
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 'var(--radius-md)' }}>
        <Table size={size} sx={{ minWidth: 650, ...tableSx }} aria-label="notes table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "50px" }}>
                <IconButton
                  size="small"
                  onClick={toggleAllRows}
                  title={allExpanded ? "Collapse All" : "Expand All"}
                >
                  {allExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
                </IconButton>
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>S.no</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>Title</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>Sequence</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>Level</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>Duration</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>Verified</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '13px' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {notes.map((note, index) => (
              <React.Fragment key={note.id}>
                <TableRow sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => toggleRow(index)}
                      title={expandedRows.has(index) ? "Collapse" : "Expand"}
                    >
                      {expandedRows.has(index) ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>{((pagination.page - 1) * pagination.limit) + index + 1}</TableCell>
                  <TableCell>
                    <Typography
                      onClick={() => toggleRow(index)}
                      sx={{ cursor: "pointer", color: "#0d6efd", fontWeight: 500 }}
                    >
                      {note.title || 'Untitled Note'}
                    </Typography>
                  </TableCell>
                  <TableCell>{note.sequence || '-'}</TableCell>
                  <TableCell>{note.level || '-'}</TableCell>
                  <TableCell>{note.duration ? `${note.duration} min` : '-'}</TableCell>
                  <TableCell><StatusBadge status={note.status} /></TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={note.verified || false}
                      disabled={!isAdmin}
                      onChange={() => handleVerifiedChange(note.id, note.verified)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <Tooltip title="Add Content">
                        <IconButton
                          size="small"
                        onClick={() => handleAddContent(note)}
                          sx={{ color: '#28a745' }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Comments">
                        <IconButton
                          size="small"
                        onClick={() => handleOpenCommentPopup('notes', note.id)}
                          sx={{ position: 'relative' }}
                      >
                          <CommentIcon fontSize="small" />
                        {note.resolved !== null && note.resolved > 0 && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: '-6px',
                                right: '-6px',
                                backgroundColor: '#dc3545',
                                color: '#fff',
                                borderRadius: '999px',
                                padding: '0 5px',
                                fontSize: '10px',
                                fontWeight: 600,
                                minWidth: '18px',
                                height: '18px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              {note.resolved}
                            </Box>
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Clone Content">
                        <IconButton
                          size="small"
                        onClick={() => handleOpenClonePopup('notes', note.id)}
                          sx={{ color: '#6c757d' }}
                        >
                          <FileCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={9} sx={{ py: 0, borderBottom: 'none' }}>
                    <Collapse in={expandedRows.has(index)} timeout="auto" unmountOnExit>
                      <Box sx={{ padding: "20px" }}>
                        <Box sx={{ padding: "20px", backgroundColor: "#f8f9fa", borderRadius: "8px", marginBottom: "15px" }}>
                          <Typography variant="h6" sx={{ color: '#6c757d', mb: 3, fontSize: '14px' }}>Note Content</Typography>
                          <Box sx={{ border: "1px solid #dee2e6", borderRadius: "6px", overflow: "hidden" }}>
                            {typeof JoditProEditor !== 'undefined' ? (
                              <JoditProEditor value={note.content || ''} config={editorConfig} />
                            ) : (
                              <Alert severity="warning" sx={{ mb: 0 }}>
                                Jodit Pro Editor is not available. Please check your license and refresh the page.
                              </Alert>
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Button variant="contained" color="success" size="small" title="Clone Note">
                            📋 Clone
                          </Button>
                        </Box>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination.totalPages > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 3, mt: 3 }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
          >
            Previous
          </Button>
          <Typography variant="body2" color="text.secondary">
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} total notes)
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page === pagination.totalPages}
          >
            Next
          </Button>
        </Box>
      )}

      <ConfirmDialog
        open={confirmationModal.isOpen}
        onClose={() => setConfirmationModal({ isOpen: false, noteId: null, currentVerified: null })}
        onConfirm={handleConfirmVerifiedChange}
        title="Confirm Verification Status Change"
        message={`Are you sure you want to ${confirmationModal.currentVerified ? 'unverify' : 'verify'} this note?\n\n`}
        severity="warning"
      />

      <ConfirmDialog
        open={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: '' })}
        title="Error"
        message={errorModal.message}
        variant="alert"
        severity="error"
      />
    </Box>
  );
};

const QUESTION_TABLE_VARIANTS = {
  mcq: {
    cacheKey: 'mcq',
    questionTypeKey: ContentTypeConstants.MCQ.key,
    browseLabel: 'MCQs',
    loadingLabel: 'Loading MCQs...',
    emptyTitle: 'No MCQs found',
    totalSuffix: 'MCQs',
    verifyLabel: 'MCQ',
  },
  quiz: {
    cacheKey: 'quiz',
    questionTypeKey: ContentTypeConstants.QUIZ.key,
    browseLabel: 'quizzes',
    loadingLabel: 'Loading quizzes...',
    emptyTitle: 'No quizzes found',
    totalSuffix: 'quizzes',
    verifyLabel: 'quiz',
  },
};

function getQuestionDisplayData(variant, questionData) {
  if (variant === 'quiz') {
    const parsed = parseQuizQuestionData(questionData || {});
    return {
      questionText: parsed.question || 'No question available',
      options: parsed.options,
      explanation: parsed.explanation || 'No explanation available',
      isQuiz: true,
    };
  }

  return {
    questionText: questionData?.question || 'No question available',
    options: questionData?.options,
    correctAnswer: questionData?.correct_answer,
    explanation: questionData?.Explanation || 'No explanation available',
    isQuiz: false,
  };
}

function renderQuestionOptions(displayData) {
  if (displayData.isQuiz) {
    if (!displayData.options || !Array.isArray(displayData.options)) return null;
    return displayData.options.map((option, idx) => {
      const label = String.fromCharCode(65 + idx);
      const isCorrect = option.correct;
      return (
        <Box key={option.id || label} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography sx={{ mr: 1, fontWeight: isCorrect ? 600 : 400, color: isCorrect ? '#28a745' : 'inherit' }}>
            {label}.
          </Typography>
          <Typography sx={{ fontWeight: isCorrect ? 600 : 400, color: isCorrect ? '#28a745' : 'inherit' }}>
            {option.text}
          </Typography>
        </Box>
      );
    });
  }

  if (!displayData.options || !Array.isArray(displayData.options)) return null;
  return displayData.options.map((option, idx) => {
    const label = String.fromCharCode(65 + idx);
    const isCorrect = option === displayData.correctAnswer;
    return (
      <Box key={label} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography sx={{ mr: 1, fontWeight: isCorrect ? 600 : 400, color: isCorrect ? '#28a745' : 'inherit' }}>
          {label}.
        </Typography>
        <Typography sx={{ fontWeight: isCorrect ? 600 : 400, color: isCorrect ? '#28a745' : 'inherit' }}>
          {option}
        </Typography>
      </Box>
    );
  });
}

// MCQ / Quiz Table Component
const QuestionTable = ({ isAdmin, variant = 'mcq' }) => {
  const variantConfig = QUESTION_TABLE_VARIANTS[variant] || QUESTION_TABLE_VARIANTS.mcq;
  const { filters, subjects } = useContext(FilterContext);
  const { refreshTrigger } = useContext(RefreshContext);
  const navigate = useNavigate();
  const { size, tableSx } = TABLE_STYLES;
  const [mcqs, setMcqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [allExpanded, setAllExpanded] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    questionId: null,
    currentVerified: null
  });
  const [errorModal, setErrorModal] = useState({
    isOpen: false,
    message: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const fetchMCQs = useCallback(async ({ force = false } = {}) => {
    if (!filters.subject_id) return;

    const cacheKey = makeContentBankCacheKey(variantConfig.cacheKey, filters, pagination);
    // Stale-while-revalidate: paint cache immediately, then always refetch quietly
    let hadCache = false;
    if (!force) {
      const cached = getContentBankCache(cacheKey);
      if (cached) {
        hadCache = true;
        setMcqs(cached.rows || []);
        setPagination((prev) => ({
          ...prev,
          total: cached.total || 0,
          totalPages: cached.totalPages || 0,
        }));
        setLoading(false);
      }
    }
    
    if (!hadCache) setLoading(true);
    try {
      const payload = {
        subject_id: filters.subject_id,
        question_type: variantConfig.questionTypeKey,
        page: pagination.page,
        limit: pagination.limit
      };

      if (filters.topic_id) payload.topic_id = filters.topic_id;
      if (filters.sub_topic_id) payload.sub_topic_id = filters.sub_topic_id;
      if (filters.level) payload.level = filters.level;

      const response = await apiPost(`${config.API_BASE_URL}/api/get-questions-hierarchical/`, payload);
      
      if (response.status === 200) {
        const rows = response.data.questions || [];
        const total = response.data.total_questions || 0;
        const totalPages = response.data.total_pages || 0;
        setMcqs(rows);
        setPagination(prev => ({
          ...prev,
          total,
          totalPages,
        }));
        setContentBankCache(cacheKey, { rows, total, totalPages });
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [filters.subject_id, filters.topic_id, filters.sub_topic_id, filters.level, pagination.page, pagination.limit, variantConfig.cacheKey, variantConfig.questionTypeKey]);

  useEffect(() => {
    fetchMCQs();
  }, [fetchMCQs]);

  // Refresh questions when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger > 0) {
      invalidateContentBankCache(variantConfig.cacheKey);
      fetchMCQs({ force: true });
    }
  }, [refreshTrigger, fetchMCQs, variantConfig.cacheKey]);

  const toggleRow = (index) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  const toggleAllRows = () => {
    if (allExpanded) {
      setExpandedRows(new Set());
    } else {
      setExpandedRows(new Set(mcqs.map((_, index) => index)));
    }
    setAllExpanded(!allExpanded);
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleVerifiedChange = (questionId, currentVerified) => {
    if (!isAdmin) return;
    setConfirmationModal({
      isOpen: true,
      questionId,
      currentVerified
    });
  };

  const handleConfirmVerifiedChange = async () => {
    const { questionId, currentVerified } = confirmationModal;
    const newVerified = !currentVerified;
    
    try {
      await apiPut(`${config.API_BASE_URL}/api/update-verified/`, {
        content_type: variantConfig.questionTypeKey,
        content_id: questionId,
        verified: newVerified
      });
      
      // Update local state + cache so Back does not show stale verified flags
      setMcqs((prev) => {
        const rows = prev.map((m) =>
          m.question_id === questionId ? { ...m, verified: newVerified } : m
        );
        setContentBankCache(makeContentBankCacheKey(variantConfig.cacheKey, filters, pagination), {
          rows,
          total: pagination.total,
          totalPages: pagination.totalPages,
        });
        return rows;
      });
      
      // Close modal
      setConfirmationModal({ isOpen: false, questionId: null, currentVerified: null });
    } catch (error) {
      setConfirmationModal({ isOpen: false, questionId: null, currentVerified: null });
      setErrorModal({ isOpen: true, message: 'Failed to update verified status. Please try again.' });
    }
  };

  const handleAddContent = (mcq) => {
    try {
      // Validate required filters
      if (!filters.subject_id) {
        toast.warning('Please select a subject first');
        return;
      }
      
      if (!filters.topic_id) {
        toast.warning('Please select a topic first');
        return;
      }
      
      // Find the subject name from the subjects array
      const selectedSubject = subjects.find(subject => subject.subject_id === filters.subject_id);
      const subjectName = selectedSubject ? selectedSubject.subject_name : '';
      
      // Set session storage with content creation parameters
      sessionStorage.setItem('contentCreation_contentType', JSON.stringify(variantConfig.questionTypeKey));
      sessionStorage.setItem('contentCreation_selectedDifficulty', JSON.stringify(mcq?.level || filters.level || 'level1'));
      sessionStorage.setItem('contentCreation_selectedSubTopic', JSON.stringify(filters.sub_topic_id || ''));
      sessionStorage.setItem('contentCreation_selectedTopic', JSON.stringify(filters.topic_id));
      if (mcq?.question_id) {
        sessionStorage.setItem('contentCreation_selectedQuestion', JSON.stringify(mcq.question_id.toString()));
      } else {
        sessionStorage.removeItem('contentCreation_selectedQuestion');
        sessionStorage.removeItem('currentQuestionId');
      }
      sessionStorage.setItem('contentCreation_subjectId', JSON.stringify(filters.subject_id));
      sessionStorage.setItem('contentCreation_subjectName', JSON.stringify(subjectName));
      
      // Mark subject flow so sidebar/back stay on Content Bank
      sessionStorage.setItem('contentCreation_navSource', 'content-bank');
      sessionStorage.removeItem('contentCreation_freezeFilters');
      sessionStorage.removeItem('contentCreation_projectId');
      sessionStorage.removeItem('contentCreation_projectName');
      
      // Navigate to content creation page with subject data
      navigate('/content-creation', {
        state: {
          data: {
            subject_id: filters.subject_id,
            subject_name: subjectName,
            freezeFilters: false // Explicitly set to false to ensure filters are not frozen
          }
        }
      });
    } catch (error) {
      toast.error('Error occurred while navigating to content creation');
    }
  };

  // Helper function to truncate text
  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  if (!filters.subject_id) {
    return (
      <EmptyState
        compact
        icon={FilterListOutlinedIcon}
        title="Select a subject"
        description={`Choose a subject from the filters above to browse ${variantConfig.browseLabel}.`}
      />
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 5 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {variantConfig.loadingLabel}
        </Typography>
      </Box>
    );
  }

  if (mcqs.length === 0) {
    return (
      <EmptyState
        compact
        title={variantConfig.emptyTitle}
        description="Try adjusting topic, sub-topic, or level filters, or add new content."
        action={<AddNewContentButton onClick={() => navigateToNewContent({ navigate, subjects, filters, contentType: variantConfig.questionTypeKey })} label={`Add ${variantConfig.browseLabel}`} />}
      />
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 'var(--radius-md)' }}>
        <Table size={size} sx={{ minWidth: 650, ...tableSx }} aria-label="mcq table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "50px" }}>
                <IconButton
                  size="small"
                  onClick={toggleAllRows}
                  title={allExpanded ? "Collapse All" : "Expand All"}
                >
                  {allExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
                </IconButton>
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>S.no</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>Question</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>Level</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>Tags</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>Tests Assigned</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>Verified</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '13px' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mcqs.map((mcq, index) => {
              const displayData = getQuestionDisplayData(variant, mcq.question_data || {});
              const questionText = displayData.questionText;
              const tags = mcq.tags || '';
              return (
                <React.Fragment key={mcq.question_id}>
                  <TableRow sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => toggleRow(index)}
                        title={expandedRows.has(index) ? "Collapse" : "Expand"}
                      >
                        {expandedRows.has(index) ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{((pagination.page - 1) * pagination.limit) + index + 1}</TableCell>
                    <TableCell>
                      <Typography
                        onClick={() => toggleRow(index)}
                        sx={{ cursor: "pointer", fontWeight: 500, color: "#212529" }}
                      >
                        {truncateText(questionText, 80)}
                      </Typography>
                    </TableCell>
                    <TableCell>{mcq.level || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {truncateText(tags, 30)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={mcq.tests_assigned || 0}
                        size="small"
                        sx={{
                          backgroundColor: '#e3f2fd',
                          color: '#1976d2',
                          fontWeight: 600,
                          fontSize: '12px'
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={mcq.verified || false}
                        disabled={!isAdmin}
                        onChange={() => handleVerifiedChange(mcq.question_id, mcq.verified)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <Tooltip title="Add Content">
                          <IconButton
                            size="small"
                            onClick={() => handleAddContent(mcq)}
                            sx={{ color: '#28a745' }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Comments">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenCommentPopup('question', mcq.question_id)}
                            sx={{ position: 'relative' }}
                        >
                            <CommentIcon fontSize="small" />
                          {mcq.resolved !== null && mcq.resolved > 0 && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: '-6px',
                                  right: '-6px',
                                  backgroundColor: '#dc3545',
                                  color: '#fff',
                                  borderRadius: '999px',
                                  padding: '0 5px',
                                  fontSize: '10px',
                                  fontWeight: 600,
                                  minWidth: '18px',
                                  height: '18px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                {mcq.resolved}
                              </Box>
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Clone Content">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenClonePopup('question', mcq.question_id)}
                            sx={{ color: '#6c757d' }}
                          >
                            <FileCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 0, borderBottom: 'none' }}>
                      <Collapse in={expandedRows.has(index)} timeout="auto" unmountOnExit>
                        <Box sx={{ padding: "20px" }}>
                          <Box sx={{ marginBottom: "16px" }}>
                            <Typography variant="h6" sx={{ color: '#6c757d', mb: 2, fontSize: '14px' }}>Question</Typography>
                            <Box sx={{ border: "1px solid #dee2e6", borderRadius: "4px", padding: "12px", backgroundColor: "#fff" }}>
                              <Typography>{questionText}</Typography>
                            </Box>
                          </Box>
                          <Box sx={{ marginBottom: "16px" }}>
                            <Typography variant="h6" sx={{ color: '#6c757d', mb: 2, fontSize: '14px' }}>Options</Typography>
                            <Box sx={{ border: "1px solid #dee2e6", borderRadius: "4px", padding: "12px", backgroundColor: "#fff" }}>
                              {renderQuestionOptions(displayData)}
                            </Box>
                          </Box>
                          <Box>
                            <Typography variant="h6" sx={{ color: '#6c757d', mb: 2, fontSize: '14px' }}>Explanation</Typography>
                            <Box sx={{ border: "1px solid #dee2e6", borderRadius: "4px", padding: "12px", backgroundColor: "#fff" }}>
                              <Typography>{displayData.explanation}</Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                            <Button variant="contained" color="success" size="small" title="Clone MCQ">
                              📋 Clone
                            </Button>
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination.totalPages > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 3, mt: 3 }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
          >
            Previous
          </Button>
          <Typography variant="body2" color="text.secondary">
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} total {variantConfig.totalSuffix})
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page === pagination.totalPages}
          >
            Next
          </Button>
        </Box>
      )}

      {/* Confirmation Modal */}
      <ConfirmDialog
        open={confirmationModal.isOpen}
        onClose={() => setConfirmationModal({ isOpen: false, questionId: null, currentVerified: null })}
        onConfirm={handleConfirmVerifiedChange}
        title="Confirm Verification Status Change"
        message={`Are you sure you want to ${confirmationModal.currentVerified ? 'unverify' : 'verify'} this ${variantConfig.verifyLabel}?\n\n`}
        severity="warning"
      />

      <ConfirmDialog
        open={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: '' })}
        title="Error"
        message={errorModal.message}
        variant="alert"
        severity="error"
      />
    </Box>
  );
};

const MCQTable = (props) => <QuestionTable {...props} variant="mcq" />;
const QuizTable = (props) => <QuestionTable {...props} variant="quiz" />;

// Coding Table Component
const CodingTable = ({ isAdmin, questionType = 'coding' }) => {
  const { filters, subjects } = useContext(FilterContext);
  const { refreshTrigger } = useContext(RefreshContext);
  const navigate = useNavigate();
  const { size, tableSx } = TABLE_STYLES;
  const [codingQuestions, setCodingQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    questionId: null,
    currentVerified: null
  });
  const [errorModal, setErrorModal] = useState({
    isOpen: false,
    message: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const fetchCodingQuestions = useCallback(async ({ force = false } = {}) => {
    if (questionType !== 'project_coding' && !filters.subject_id) return;

    const cacheKey = makeContentBankCacheKey(
      questionType === 'project_coding' ? 'project_coding' : 'coding',
      { ...filters, questionType },
      pagination
    );
    // Stale-while-revalidate: paint cache immediately, then always refetch quietly
    let hadCache = false;
    if (!force) {
      const cached = getContentBankCache(cacheKey);
      if (cached) {
        hadCache = true;
        setCodingQuestions(cached.rows || []);
        setPagination((prev) => ({
          ...prev,
          total: cached.total || 0,
          totalPages: cached.totalPages || 0,
        }));
        setLoading(false);
      }
    }
    
    if (!hadCache) setLoading(true);
    try {
      const payload = {
        question_type: questionType,
        page: pagination.page,
        limit: pagination.limit
      };

      if (filters.subject_id) payload.subject_id = filters.subject_id;
      if (filters.topic_id) payload.topic_id = filters.topic_id;
      if (filters.sub_topic_id) payload.sub_topic_id = filters.sub_topic_id;
      if (filters.level) payload.level = filters.level;

      const response = await apiPost(`${config.API_BASE_URL}/api/get-questions-hierarchical/`, payload);
      
      if (response.status === 200) {
        const rows = response.data.questions || [];
        const total = response.data.total_questions || 0;
        const totalPages = response.data.total_pages || 0;
        setCodingQuestions(rows);
        setPagination(prev => ({
          ...prev,
          total,
          totalPages,
        }));
        setContentBankCache(cacheKey, { rows, total, totalPages });
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [filters.subject_id, filters.topic_id, filters.sub_topic_id, filters.level, pagination.page, pagination.limit, questionType]);

  useEffect(() => {
    fetchCodingQuestions();
  }, [fetchCodingQuestions]);

  // Refresh coding questions when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger > 0) {
      invalidateContentBankCache(questionType === 'project_coding' ? 'project_coding' : 'coding');
      fetchCodingQuestions({ force: true });
    }
  }, [refreshTrigger, fetchCodingQuestions, questionType]);

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleVerifiedChange = (questionId, currentVerified) => {
    if (!isAdmin) return;
    setConfirmationModal({
      isOpen: true,
      questionId,
      currentVerified
    });
  };

  const handleConfirmVerifiedChange = async () => {
    const { questionId, currentVerified } = confirmationModal;
    const newVerified = !currentVerified;
    
    try {
      await apiPut(`${config.API_BASE_URL}/api/update-verified/`, {
        content_type: questionType === 'project_coding' ? 'project_coding' : 'coding',
        content_id: questionId,
        verified: newVerified
      });
      
      // Update local state + cache so Back does not show stale verified flags
      setCodingQuestions((prev) => {
        const rows = prev.map((q) =>
          q.question_id === questionId ? { ...q, verified: newVerified } : q
        );
        setContentBankCache(
          makeContentBankCacheKey(
            questionType === 'project_coding' ? 'project_coding' : 'coding',
            { ...filters, questionType },
            pagination
          ),
          {
            rows,
            total: pagination.total,
            totalPages: pagination.totalPages,
          }
        );
        return rows;
      });
      
      // Close modal
      setConfirmationModal({ isOpen: false, questionId: null, currentVerified: null });
    } catch (error) {
      setConfirmationModal({ isOpen: false, questionId: null, currentVerified: null });
      setErrorModal({ isOpen: true, message: 'Failed to update verified status. Please try again.' });
    }
  };

  const handleAddContent = (codingQuestion) => {
    try {
      // Validate required filters
      if (!filters.subject_id) {
        toast.warning('Please select a subject first');
        return;
      }
      
      if (!filters.topic_id) {
        toast.warning('Please select a topic first');
        return;
      }
      
      // Find the subject name from the subjects array
      const selectedSubject = subjects.find(subject => subject.subject_id === filters.subject_id);
      const subjectName = selectedSubject ? selectedSubject.subject_name : '';
      
      // Set session storage with content creation parameters
      sessionStorage.setItem('contentCreation_contentType', JSON.stringify(questionType === 'project_coding' ? ContentTypeConstants.PROJECT_CODING.key : ContentTypeConstants.CODING.key));
      sessionStorage.setItem('contentCreation_selectedDifficulty', JSON.stringify(codingQuestion?.level || filters.level || 'level1'));
      sessionStorage.setItem('contentCreation_selectedSubTopic', JSON.stringify(filters.sub_topic_id || ''));
      sessionStorage.setItem('contentCreation_selectedTopic', JSON.stringify(filters.topic_id));
      if (codingQuestion?.question_id) {
        sessionStorage.setItem('contentCreation_selectedQuestion', JSON.stringify(codingQuestion.question_id.toString()));
      } else {
        sessionStorage.removeItem('contentCreation_selectedQuestion');
        sessionStorage.removeItem('currentQuestionId');
      }
      sessionStorage.setItem('contentCreation_subjectId', JSON.stringify(filters.subject_id));
      sessionStorage.setItem('contentCreation_subjectName', JSON.stringify(subjectName));

      // Persist editor so Choose Editor is populated when opening from Content Bank
      const editorFromQuestion =
        codingQuestion?.question_data?.editor ||
        codingQuestion?.editor ||
        codingQuestion?.question_types_enum ||
        null;
      if (questionType !== 'project_coding' && editorFromQuestion) {
        sessionStorage.setItem('contentCreation_selectedEditor', JSON.stringify(editorFromQuestion));
      } else if (questionType !== 'project_coding') {
        sessionStorage.removeItem('contentCreation_selectedEditor');
      }
      
      // Store description for project_coding questions
      if (questionType === 'project_coding' && codingQuestion?.description) {
        sessionStorage.setItem('contentCreation_projectCodingDescription', JSON.stringify(codingQuestion.description));
      } else {
        sessionStorage.removeItem('contentCreation_projectCodingDescription');
      }
      
      // Mark subject flow so sidebar/back stay on Content Bank
      sessionStorage.setItem('contentCreation_navSource', 'content-bank');
      sessionStorage.removeItem('contentCreation_freezeFilters');
      sessionStorage.removeItem('contentCreation_projectId');
      sessionStorage.removeItem('contentCreation_projectName');
      sessionStorage.removeItem('contentCreation_isReviewReadOnly');
      
      // Navigate to content creation page with subject data
      navigate('/content-creation', {
        state: {
          data: {
            subject_id: filters.subject_id,
            subject_name: subjectName,
            freezeFilters: false, // Explicitly set to false to ensure filters are not frozen
            ...(editorFromQuestion ? { editor: editorFromQuestion } : {}),
          }
        }
      });
    } catch (error) {
      toast.error('Error occurred while navigating to content creation');
    }
  };

  const handleCheckReview = (codingQuestion) => {
    try {
      // Validate required filters
      if (!filters.subject_id) {
        toast.warning('Please select a subject first');
        return;
      }
      
      if (!filters.topic_id) {
        toast.warning('Please select a topic first');
        return;
      }

      if (questionType === 'project_coding') {
        const selectedSubject = subjects.find(
          (subject) => subject.subject_id === filters.subject_id
        );
        const subjectName = selectedSubject ? selectedSubject.subject_name : '';
        const questionId = codingQuestion.question_id;

        sessionStorage.setItem(
          'contentCreation_contentType',
          JSON.stringify(ContentTypeConstants.PROJECT_CODING.key)
        );
        sessionStorage.setItem(
          'contentCreation_selectedDifficulty',
          JSON.stringify(codingQuestion.level || 'level1')
        );
        sessionStorage.setItem(
          'contentCreation_selectedSubTopic',
          JSON.stringify(filters.sub_topic_id || '')
        );
        sessionStorage.setItem(
          'contentCreation_selectedTopic',
          JSON.stringify(filters.topic_id)
        );
        sessionStorage.setItem(
          'contentCreation_selectedQuestion',
          JSON.stringify(String(questionId))
        );
        sessionStorage.setItem(
          'contentCreation_subjectId',
          JSON.stringify(filters.subject_id)
        );
        sessionStorage.setItem(
          'contentCreation_subjectName',
          JSON.stringify(subjectName)
        );
        sessionStorage.removeItem('contentCreation_isReviewReadOnly');
        sessionStorage.setItem('contentCreation_navSource', 'content-bank');
        sessionStorage.removeItem('contentCreation_freezeFilters');
        sessionStorage.removeItem('contentCreation_projectId');
        sessionStorage.removeItem('contentCreation_projectName');
        if (codingQuestion.description) {
          sessionStorage.setItem(
            'contentCreation_projectCodingDescription',
            JSON.stringify(codingQuestion.description)
          );
        } else {
          sessionStorage.removeItem('contentCreation_projectCodingDescription');
        }

        navigate('/content-creation', {
          state: {
            data: {
              subject_id: filters.subject_id,
              subject_name: subjectName,
              topic_id: filters.topic_id,
              sub_topic_id: filters.sub_topic_id || '',
              content_type: ContentTypeConstants.PROJECT_CODING.key,
              contentId: String(questionId),
            },
          },
        });
        return;
      }
      
      // Route based on subject - Python uses existing editor; others use web editor testing route
      const subjectIdLower = String(filters.subject_id).toLowerCase();
      const isPython = subjectIdLower.startsWith('py');

      if (isPython) {
        // Store coding question parameters in sessionStorage
        sessionStorage.setItem('codingQuestions_subjectId', JSON.stringify(filters.subject_id));
        sessionStorage.setItem('codingQuestions_topicId', JSON.stringify(filters.topic_id));
        sessionStorage.setItem('codingQuestions_subTopicId', JSON.stringify(filters.sub_topic_id || ''));
        sessionStorage.setItem('codingQuestions_level', JSON.stringify(filters.level || ''));
        sessionStorage.setItem('codingQuestions_questionId', JSON.stringify(codingQuestion.question_id));
        sessionStorage.setItem('codingQuestions_editor', JSON.stringify('python'));
        sessionStorage.setItem('codingQuestions_returnPath', '/content-bank');
        navigate('/coding-questions');
        return;
      }

      // Build testing payload for HTML/CSS/JS editor
      const mappedQuestions = (codingQuestions || []).map((q) => {
        const qd = q.question_data || {};
        const tabs = qd.Tabs && Array.isArray(qd.Tabs) && qd.Tabs.length > 0
          ? qd.Tabs
          : [
              { name: 'index.html', type: 'HTML' },
              { name: 'styles.css', type: 'CSS' },
              { name: 'script.js', type: 'JS' }
            ];
        const codeValidation = {};
        tabs.forEach((t) => {
          codeValidation[t.name] = { template: '' };
        });

        return {
          Qn_name: q.question_id,
          Page_Name: qd.Page_Name || 'HTML/CSS Question',
          level: q.level || qd.level || 'level1',
          subtopic_id: q.sub_topic_id || qd.subtopic_id || '',
          type: questionType === 'project_coding' ? 'project_coding' : 'coding',
          Tabs: tabs,
          Qn: qd.Qn || qd.question || '',
          requirements: qd.requirements || '',
          Code_Validation: qd.Code_Validation || codeValidation,
          defaulttemplate: qd.Template || qd.defaulttemplate || '',
          image_path: qd.image_path || '',
          video_path: qd.video_path || '',
          CreatedBy: qd.CreatedBy || '',
          CreatedOn: qd.CreatedON || '',
          LastUpdated: qd.LastUpdated || '',
          status: false,
          entered_ans: {},
          image_urls: qd.image_urls || [],
          currentFile: qd.currentFile || ''
        };
      });

      // Persist to session storage for testing editor to consume
      sessionStorage.setItem('codingQuestions', JSON.stringify(mappedQuestions));
      const index = (codingQuestions || []).findIndex((q) => q.question_id === codingQuestion.question_id);
      sessionStorage.setItem('currentQuestionIndex', String(index >= 0 ? index : 0));

      // Navigate to web editor route
      navigate('/coding-web');
    } catch (error) {
      toast.error('Error occurred while navigating to coding questions');
    }
  };

  // Helper function to truncate text
  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  if (questionType !== 'project_coding' && !filters.subject_id) {
    return (
      <EmptyState
        compact
        icon={FilterListOutlinedIcon}
        title="Select a subject"
        description="Choose a subject from the filters above to browse coding questions."
      />
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 5 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading {questionType === 'project_coding' ? 'project coding' : 'coding'} questions...
        </Typography>
      </Box>
    );
  }

  if (codingQuestions.length === 0) {
    return (
      <EmptyState
        compact
        title={`No ${questionType === 'project_coding' ? 'project coding' : 'coding'} questions found`}
        description="Try adjusting your filters or create new content."
        action={
          <AddNewContentButton
            onClick={() => navigateToNewContent({
              navigate,
              subjects,
              filters,
              contentType: questionType === 'project_coding'
                ? ContentTypeConstants.PROJECT_CODING.key
                : ContentTypeConstants.CODING.key,
            })}
            label={`Add ${questionType === 'project_coding' ? 'project coding' : 'coding'} question`}
          />
        }
      />
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 'var(--radius-md)' }}>
        <Table size={size} sx={{ minWidth: 650, ...tableSx }} aria-label="coding questions table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>S.no</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>Question</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>Level</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>Tags</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>Tests Assigned</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>Verified</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>Review</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '13px' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {codingQuestions.map((codingQuestion, index) => {
              const questionData = codingQuestion.question_data || {};
              const questionText = questionData.Qn || 'No question available';
              const tags = codingQuestion.tags || '';
              return (
                <TableRow key={codingQuestion.question_id} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                  <TableCell>{((pagination.page - 1) * pagination.limit) + index + 1}</TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 500 }}>{truncateText(questionText, 80)}</Typography>
                  </TableCell>
                  <TableCell>{codingQuestion.level || '-'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {truncateText(tags, 30)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={codingQuestion.tests_assigned || 0}
                      size="small"
                      sx={{
                        backgroundColor: '#e3f2fd',
                        color: '#1976d2',
                        fontWeight: 600,
                        fontSize: '12px'
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={codingQuestion.verified || false}
                      disabled={!isAdmin}
                      onChange={() => handleVerifiedChange(codingQuestion.question_id, codingQuestion.verified)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleCheckReview(codingQuestion)}
                      sx={{ borderRadius: '20px' }}
                    >
                      Check
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <Tooltip title="Add Content">
                        <IconButton
                          size="small"
                        onClick={() => handleAddContent(codingQuestion)}
                          sx={{ color: '#28a745' }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Comments">
                        <IconButton
                          size="small"
                        onClick={() => handleOpenCommentPopup('question', codingQuestion.question_id)}
                          sx={{ position: 'relative' }}
                      >
                          <CommentIcon fontSize="small" />
                        {codingQuestion.resolved !== null && codingQuestion.resolved > 0 && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: '-6px',
                                right: '-6px',
                                backgroundColor: '#dc3545',
                                color: '#fff',
                                borderRadius: '999px',
                                padding: '0 5px',
                                fontSize: '10px',
                                fontWeight: 600,
                                minWidth: '18px',
                                height: '18px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              {codingQuestion.resolved}
                            </Box>
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Clone Content">
                        <IconButton
                          size="small"
                        onClick={() => handleOpenClonePopup('question', codingQuestion.question_id)}
                          sx={{ color: '#6c757d' }}
                        >
                          <FileCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination.totalPages > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 3, mt: 3 }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
          >
            Previous
          </Button>
          <Typography variant="body2" color="text.secondary">
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} total {questionType === 'project_coding' ? 'project coding' : 'coding'} questions)
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page === pagination.totalPages}
          >
            Next
          </Button>
        </Box>
      )}

      {/* Confirmation Modal */}
      <ConfirmDialog
        open={confirmationModal.isOpen}
        onClose={() => setConfirmationModal({ isOpen: false, questionId: null, currentVerified: null })}
        onConfirm={handleConfirmVerifiedChange}
        title="Confirm Verification Status Change"
        message={`Are you sure you want to ${confirmationModal.currentVerified ? 'unverify' : 'verify'} this ${questionType === 'project_coding' ? 'project coding' : 'coding'} question?\n\nThis action will update the verified status.`}
        severity="warning"
      />

      <ConfirmDialog
        open={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: '' })}
        title="Error"
        message={errorModal.message}
        variant="alert"
        severity="error"
      />
    </Box>
  );
};

// Project Coding Table Component (wrapper for CodingTable with project_coding type)
const ProjectCodingTable = ({ isAdmin }) => {
  return <CodingTable isAdmin={isAdmin} questionType="project_coding" />;
};

// Comment popup functions (defined outside component to be accessible by child components)
let setCommentPopupState = null;

const handleOpenCommentPopup = (referenceType, referenceId) => {
  // Validate that we have valid data before opening popup
  if (!referenceType || !referenceId || referenceId === null || referenceId === undefined) {
    toast.error('Unable to open comments. Invalid reference data.');
    return;
  }
  
  if (setCommentPopupState) {
    setCommentPopupState({
      isOpen: true,
      referenceType,
      referenceId: String(referenceId) // Ensure it's a string
    });
  }
};

const handleCloseCommentPopup = (triggerRefresh) => {
  if (setCommentPopupState) {
    setCommentPopupState({
      isOpen: false,
      referenceType: null,
      referenceId: null
    });
  }
  
  // Trigger refresh after popup closes
  if (triggerRefresh) {
    triggerRefresh();
  }
};

// Clone popup functions
let setClonePopupState = null;

const handleOpenClonePopup = (referenceType, referenceId) => {
  // Validate that we have valid data before opening popup
  if (!referenceType || !referenceId || referenceId === null || referenceId === undefined) {
    toast.error('Unable to open clone popup. Invalid reference data.');
    return;
  }
  
  if (setClonePopupState) {
    setClonePopupState({
      isOpen: true,
      referenceType,
      referenceId: String(referenceId) // Ensure it's a string
    });
  }
};

const handleCloseClonePopup = (triggerRefresh) => {
  if (setClonePopupState) {
    setClonePopupState({
      isOpen: false,
      referenceType: null,
      referenceId: null
    });
  }
  
  // Trigger refresh after popup closes
  if (triggerRefresh) {
    triggerRefresh();
  }
};

const CONTENT_BANK_TAB_ORDER = [
  ContentTypeConstants.VIDEO,
  ContentTypeConstants.NOTES,
  ContentTypeConstants.MCQ,
  ContentTypeConstants.QUIZ,
  ContentTypeConstants.CODING,
  ContentTypeConstants.PROJECT_CODING,
];

const CONTENT_BANK_TAB_KEYS = new Set(CONTENT_BANK_TAB_ORDER.map((tab) => tab.key));
const CONTENT_BANK_ACTIVE_TAB_KEY = 'contentBank_activeTab';

function readStoredContentBankTab() {
  try {
    // When returning from content creation, prefer the type that was being edited
    const navSource = sessionStorage.getItem('contentCreation_navSource');
    if (navSource === 'content-bank') {
      const creationType = sessionStorage.getItem('contentCreation_contentType');
      if (creationType) {
        const parsedType = JSON.parse(creationType);
        if (CONTENT_BANK_TAB_KEYS.has(parsedType)) {
          sessionStorage.setItem(CONTENT_BANK_ACTIVE_TAB_KEY, JSON.stringify(parsedType));
          return parsedType;
        }
      }
    }

    const stored = sessionStorage.getItem(CONTENT_BANK_ACTIVE_TAB_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (CONTENT_BANK_TAB_KEYS.has(parsed)) return parsed;
    }
  } catch {
    // ignore
  }
  return ContentTypeConstants.VIDEO.key;
}

function persistContentBankTab(tabKey) {
  try {
    if (CONTENT_BANK_TAB_KEYS.has(tabKey)) {
      sessionStorage.setItem(CONTENT_BANK_ACTIVE_TAB_KEY, JSON.stringify(tabKey));
    }
  } catch {
    // ignore
  }
}

// Inner component that uses filters
const ContentBankWithFilters = ({ activeTab, setActiveTab, userIsAdmin }) => {
  const navigate = useNavigate();
  const { filters, subjects } = useFilters();
  const activeTabMeta = CONTENT_BANK_TAB_ORDER.find((tab) => tab.key === activeTab);
  const addLabel = `Add ${activeTabMeta?.label || 'content'}`;

  return (
    <Box sx={layoutStyles.container}>
      <Paper
        variant="outlined"
        sx={{
          flexShrink: 0,
          p: 2,
          borderRadius: 'var(--radius-lg)',
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        }}
      >
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
          Filters
        </Typography>
        <HierarchicalFilters />
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          flexShrink: 0,
          borderTop: 0,
          borderRadius: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', pr: 1.5 }}>
          <Tabs
            value={activeTab}
            onChange={(_, value) => {
              persistContentBankTab(value);
              setActiveTab(value);
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              flex: 1,
              minHeight: 48,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.875rem',
                minHeight: 48,
              },
            }}
          >
            {CONTENT_BANK_TAB_ORDER.map((tab) => (
              <Tab key={tab.key} value={tab.key} label={tab.label} />
            ))}
          </Tabs>
          <AddNewContentButton
            onClick={() => navigateToNewContent({ navigate, subjects, filters, contentType: activeTab })}
            label={addLabel}
          />
        </Box>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          flex: 1,
          minHeight: 0,
          borderTop: 0,
          borderRadius: 'var(--radius-lg)',
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={layoutStyles.tabContent}>
          {activeTab === ContentTypeConstants.VIDEO.key && <VideosTable isAdmin={userIsAdmin} />}
          {activeTab === ContentTypeConstants.NOTES.key && <NotesTable isAdmin={userIsAdmin} />}
          {activeTab === ContentTypeConstants.MCQ.key && <MCQTable isAdmin={userIsAdmin} />}
          {activeTab === ContentTypeConstants.QUIZ.key && <QuizTable isAdmin={userIsAdmin} />}
          {activeTab === ContentTypeConstants.CODING.key && (
            <CodingTable isAdmin={userIsAdmin} questionType={ContentTypeConstants.CODING.key} />
          )}
          {activeTab === ContentTypeConstants.PROJECT_CODING.key && (
            <ProjectCodingTable isAdmin={userIsAdmin} />
          )}
        </Box>
      </Paper>
    </Box>
  );
};

// Main ContentBank Component (Inner component that uses RefreshContext)
const ContentBankInner = () => {
  const [activeTab, setActiveTab] = useState(readStoredContentBankTab);
  const { triggerRefresh } = useContext(RefreshContext);
  
  // Comment popup state
  const [commentPopup, setCommentPopup] = useState({
    isOpen: false,
    referenceType: null,
    referenceId: null
  });

  // Clone popup state
  const [clonePopup, setClonePopup] = useState({
    isOpen: false,
    referenceType: null,
    referenceId: null
  });
  
  // Set the global state setters
  setCommentPopupState = setCommentPopup;
  setClonePopupState = setClonePopup;
  
  // Get user info from session storage or context
  const [userEmail, setUserEmail] = useState();
  const [userName, setUserName] = useState('User');
  const [userIsAdmin, setUserIsAdmin] = useState(false);

  // Load user info from session storage after component mounts
  useEffect(() => {
    const email = sessionStorage.getItem('Email');
    const name = sessionStorage.getItem('Name') || 'User';
    
    setUserEmail(email);
    setUserName(name);
    setUserIsAdmin(isAdmin());
  }, []);

  return (
    <FilterProvider>
      <ContentBankWithFilters 
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        userIsAdmin={userIsAdmin}
      />
      
      {/* Comment Popup */}
      <CommentPopup
        isOpen={commentPopup.isOpen}
        onClose={() => handleCloseCommentPopup(triggerRefresh)}
        onRefresh={triggerRefresh}
        referenceType={commentPopup.referenceType}
        referenceId={commentPopup.referenceId}
        userEmail={userEmail}
        userName={userName}
      />

      {/* Clone Popup */}
      <ClonePopup
        isOpen={clonePopup.isOpen}
        onClose={() => handleCloseClonePopup(triggerRefresh)}
        onRefresh={triggerRefresh}
        referenceType={clonePopup.referenceType}
        referenceId={clonePopup.referenceId}
        userEmail={userEmail}
        userName={userName}
      />
      </FilterProvider>
  );
};

// Main ContentBank Component (Wrapper that provides RefreshContext)
const ContentBank = () => {
  return (
    <RefreshProvider>
      <ContentBankInner />
    </RefreshProvider>
  );
};

export default ContentBank;

